/**
 * IntentOnboardingPage — Full-screen setup page per signup intent.
 *
 * Renders different content based on the user's signup intent:
 * - chat: Connect channels (LINE OA, Facebook, Instagram) — velocity-focused launchpad
 * - selling: Storefront preview -> add first product
 * - pos: POS terminal preview -> add menu items
 * - crm: Pipeline board preview -> add first lead
 *
 * Mobile-first (full-screen). Desktop: centered card style.
 *
 * Epic 14 Phase 9, section 7a.
 */

import { type ReactNode, useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  ChatCircleDots,
  Storefront,
  CashRegister,
  Kanban,
  ArrowRight,
  X,
  SpinnerGap,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import {
  LineIcon,
  FacebookIcon,
  InstagramIcon,
} from "@/components/icons/BrandIcons";
import { tenantServer } from "@/lib/api/tenantServerClient";
import type { OnboardingState } from "@/hooks/useOnboardingState";
import {
  captureOnboardingViewed,
  captureOnboardingCompleted,
  captureOnboardingSkipped,
} from "@/lib/analytics/posthog-events";

// -- Channel definitions for chat intent --

export interface ChannelDef {
  id: string;
  name: string;
  icon: ReactNode;
  connectHref: string;
  tint: string;
}

export const PRIMARY_CHANNELS: ChannelDef[] = [
  { id: "line_oa", name: "LINE OA", icon: <LineIcon className="w-10 h-10" />, connectHref: "/inbox/settings/inboxes/new/line", tint: "bg-[#06C755]/8 dark:bg-[#06C755]/15" },
  { id: "facebook", name: "Facebook", icon: <FacebookIcon className="w-10 h-10" />, connectHref: "/inbox/settings/inboxes/new/facebook", tint: "bg-[#1877F2]/8 dark:bg-[#1877F2]/15" },
  { id: "instagram", name: "Instagram", icon: <InstagramIcon className="w-10 h-10" />, connectHref: "/inbox/settings/inboxes/new/instagram", tint: "bg-[#E1306C]/8 dark:bg-[#E1306C]/15" },
];

// -- Seed API method per intent --

const SEED_METHODS: Record<string, string> = {
  chat: "zaviago_backend.api.onboarding_seed.seed_chat_demo_data",
};

// -- Intent content configuration --

interface IntentConfig {
  icon: typeof ChatCircleDots;
  iconBg: string;
  title: string;
  subtitle: string;
  primaryCta: string;
  primaryCtaLoading: string;
  primaryHref: string;
  secondaryCta?: string;
  secondaryHref?: string;
}

const INTENT_CONFIGS: Record<string, IntentConfig> = {
  chat: {
    icon: ChatCircleDots,
    iconBg: "bg-blue-500",
    title: "เลือกช่องทางแรกของคุณ",
    subtitle: "เชื่อมต่อแล้วเริ่มรับแชทจากลูกค้าได้ทันที",
    primaryCta: "เปิด Inbox",
    primaryCtaLoading: "กำลังเตรียม Inbox ของคุณ...",
    primaryHref: "/inbox",
  },
  selling: {
    icon: Storefront,
    iconBg: "bg-emerald-500",
    title: "เริ่มขายสินค้าออนไลน์",
    subtitle: "เพิ่มสินค้าชิ้นแรกของคุณ แล้วเปิดร้านค้าออนไลน์ให้ลูกค้าเข้าชม",
    primaryCta: "เพิ่มสินค้าชิ้นแรก",
    primaryCtaLoading: "กำลังเตรียมร้านค้า...",
    primaryHref: "/inventory/item/new",
    secondaryCta: "ดูตัวอย่างร้านค้า",
    secondaryHref: "/storefront",
  },
  pos: {
    icon: CashRegister,
    iconBg: "bg-amber-500",
    title: "ตั้งค่าระบบ POS ของคุณ",
    subtitle: "เพิ่มเมนูหรือสินค้า แล้วเริ่มใช้ระบบ POS ได้เลย",
    primaryCta: "เพิ่มเมนู/สินค้า",
    primaryCtaLoading: "กำลังเตรียมระบบ POS...",
    primaryHref: "/inventory/item/new",
    secondaryCta: "ลองใช้ POS ตัวอย่าง",
    secondaryHref: "/pos",
  },
  crm: {
    icon: Kanban,
    iconBg: "bg-violet-500",
    title: "จัดการลูกค้าของคุณ",
    subtitle: "เพิ่ม Lead คนแรก แล้วเริ่มติดตามโอกาสการขายผ่าน Pipeline",
    primaryCta: "เพิ่ม Lead คนแรก",
    primaryCtaLoading: "กำลังเตรียม Pipeline...",
    primaryHref: "/crm",
    secondaryCta: "ดูตัวอย่าง Pipeline",
    secondaryHref: "/crm",
  },
};

// -- Animation variants --

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16, scale: 0.95 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.4, ease: [0.32, 0.72, 0, 1] as [number, number, number, number] },
  },
};

// -- Main component --

interface IntentOnboardingPageProps {
  onboardingState: OnboardingState;
}

/** localStorage key for showing the floating channel guide in the inbox */
export const CHANNEL_GUIDE_KEY = "chat_channel_guide_active";

export function IntentOnboardingPage({ onboardingState }: IntentOnboardingPageProps) {
  const { intent, completeOnboarding, skipOnboarding } = onboardingState;
  const navigate = useNavigate();
  const [seeding, setSeeding] = useState(false);
  const chatRedirectStarted = useRef(false);

  // Track onboarding page view (fires once per mount)
  useEffect(() => {
    if (intent) captureOnboardingViewed(intent);
  }, [intent]);

  // Chat intent: skip this page entirely — seed demo data and go straight to inbox
  useEffect(() => {
    if (intent !== "chat" || chatRedirectStarted.current) return;
    chatRedirectStarted.current = true;

    const seedAndRedirect = async () => {
      setSeeding(true);
      const seedMethod = SEED_METHODS["chat"];
      if (seedMethod) {
        try {
          await tenantServer.call(seedMethod);
        } catch {
          // Seed failure is non-blocking — demo data is nice-to-have
        }
      }
      // Mark the floating channel guide as active so inbox shows it
      localStorage.setItem(CHANNEL_GUIDE_KEY, "1");
      captureOnboardingCompleted("chat");
      completeOnboarding();
      navigate("/inbox");
    };

    seedAndRedirect();
  }, [intent, completeOnboarding, navigate]);

  // Chat intent shows a loading state while seeding + redirecting
  if (intent === "chat") {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <div className={cn(
          "w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg bg-blue-500"
        )}>
          <ChatCircleDots size={28} weight="fill" className="text-white" />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-foreground">
            กำลังเตรียม Inbox ของคุณ...
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            เรากำลังสร้างตัวอย่างแชทให้คุณดู
          </p>
        </div>
        <SpinnerGap size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!intent) return null;

  const config = INTENT_CONFIGS[intent];
  if (!config) return null;

  const Icon = config.icon;

  const handlePrimaryCta = async () => {
    const seedMethod = SEED_METHODS[intent];

    if (seedMethod) {
      setSeeding(true);
      try {
        await tenantServer.call(seedMethod);
      } catch {
        // Seed failure is non-blocking -- demo data is nice-to-have
      } finally {
        setSeeding(false);
      }
    }

    captureOnboardingCompleted(intent);
    completeOnboarding();
    navigate(config.primaryHref);
  };

  const handleSkip = () => {
    captureOnboardingSkipped(intent);
    skipOnboarding();
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header with skip */}
      <motion.div variants={itemVariants} className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-11 h-11 rounded-2xl flex items-center justify-center shadow-lg",
            config.iconBg
          )}>
            <Icon size={24} weight="fill" className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground leading-tight">{config.title}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{config.subtitle}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSkip}
          className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground shrink-0"
          aria-label="ข้ามขั้นตอนนี้"
        >
          <X size={20} />
        </button>
      </motion.div>

      {/* Non-chat intents: Original layout (selling, pos, crm) */}
      {intent !== "chat" && (
        <>
          {/* Primary CTA */}
          <motion.button
            variants={itemVariants}
            type="button"
            onClick={handlePrimaryCta}
            disabled={seeding}
            className={cn(
              "w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl",
              "text-white font-medium text-sm",
              config.iconBg,
              "hover:opacity-90 transition-opacity",
              "active:scale-[0.98] transition-transform duration-100",
              "disabled:opacity-70 disabled:cursor-not-allowed"
            )}
          >
            {seeding ? (
              <>
                <SpinnerGap size={18} className="animate-spin" />
                {config.primaryCtaLoading}
              </>
            ) : (
              <>
                {config.primaryCta}
                <ArrowRight size={18} weight="bold" />
              </>
            )}
          </motion.button>

          {/* Secondary CTA */}
          {config.secondaryCta && config.secondaryHref && (
            <motion.div variants={itemVariants}>
              <Link
                to={config.secondaryHref}
                onClick={completeOnboarding}
                className="block text-center text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
              >
                {config.secondaryCta}
              </Link>
            </motion.div>
          )}
        </>
      )}

      {/* Skip link */}
      <motion.p variants={itemVariants} className="text-center text-xs text-muted-foreground">
        <button
          type="button"
          onClick={handleSkip}
          className="hover:text-foreground transition-colors underline"
        >
          ข้ามขั้นตอนนี้ ไปแดชบอร์ดเลย
        </button>
      </motion.p>
    </motion.div>
  );
}
